/**
 * The list's view, as the address bar holds it: which tab, which page,
 * which filters. A default is not written into the address: `/specialists`
 * is the first tab's first page.
 *
 * What the list is searched for (a name, a phone) is not part of it: that
 * is personal data, kept out of the address (`@/shared/lib/private-search`).
 */
import { idOf } from "@/shared/lib/search";

import type { CountsFilter, SpecialistsFilter } from "../api/queries";
import { SPECIALIST_STATUSES, type SpecialistCounts, type SpecialistStatus } from "../api/types";

export const LIST_TABS = ["review", "changes", "working", "suspended", "blocked", "all"] as const;
export type ListTab = (typeof LIST_TABS)[number];
export const DEFAULT_TAB: ListTab = "review";

/** The status each tab asks for; `all` asks for none of its own. */
const TAB_STATUS: Readonly<Record<Exclude<ListTab, "all">, SpecialistStatus>> = {
  review: "in_review",
  changes: "needs_changes",
  working: "approved",
  suspended: "suspended",
  blocked: "blocked",
};

export interface ListSearch {
  tab?: ListTab;
  page?: number;
  /** A service area's id: works there. */
  area?: string;
  /** A profession's id: has it, approved or not. */
  profession?: string;
  online?: "yes" | "no";
  /** A status; only the "all" tab offers it. */
  status?: SpecialistStatus;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseListSearch(raw: Readonly<Record<string, unknown>>): ListSearch {
  const search: ListSearch = {};
  const tab = LIST_TABS.find((known) => known === raw["tab"]);
  if (tab !== undefined && tab !== DEFAULT_TAB) search.tab = tab;
  const page = Number(raw["page"]);
  if (Number.isInteger(page) && page > 1) search.page = page;
  const area = idOf(raw["area"]);
  if (area !== undefined) search.area = area;
  const profession = idOf(raw["profession"]);
  if (profession !== undefined) search.profession = profession;
  if (raw["online"] === "yes" || raw["online"] === "no") search.online = raw["online"];
  const status = SPECIALIST_STATUSES.find((known) => known === raw["status"]);
  if (status !== undefined) search.status = status;
  return search;
}

export function tabOf(search: ListSearch): ListTab {
  return search.tab ?? DEFAULT_TAB;
}

/**
 * What a tab asks `GET /v2/admin/specialists` for, with the filters that are
 * set. `q` is what the list is searched for (part of the name or the phone,
 * or the whole display id): it comes from the screen, never from the address.
 */
export function tabFilter(tab: ListTab, search: ListSearch, q?: string): SpecialistsFilter {
  const filter: SpecialistsFilter = {};
  if (tab !== "all") filter.status = TAB_STATUS[tab];
  else if (search.status !== undefined) filter.status = search.status;
  return { ...filter, ...countsFilter(search, q) };
}

/**
 * The one status every row of the view has: a tab's own, or the one chosen
 * under "all". Null when the rows may differ.
 */
export function statusShown(tab: ListTab, search: ListSearch): SpecialistStatus | null {
  return tab === "all" ? (search.status ?? null) : TAB_STATUS[tab];
}

/** What the tabs' counts are asked with: the filters that are set, and no status. */
export function countsFilter(search: ListSearch, q?: string): CountsFilter {
  const filter: CountsFilter = {};
  if (search.area !== undefined) filter.area_id = search.area;
  if (search.profession !== undefined) filter.profession_id = search.profession;
  if (search.online !== undefined) filter.is_online = search.online === "yes";
  if (q !== undefined) filter.q = q;
  return filter;
}

/**
 * The number on each tab, from the service's count of every status: a tab
 * shows its own status's, and "all" every status's added up.
 */
export function tabCounts(
  counts: SpecialistCounts | undefined,
): Record<ListTab, number | undefined> {
  const all =
    counts === undefined
      ? undefined
      : SPECIALIST_STATUSES.reduce((sum, status) => sum + counts[status], 0);
  return {
    review: counts?.[TAB_STATUS.review],
    changes: counts?.[TAB_STATUS.changes],
    working: counts?.[TAB_STATUS.working],
    suspended: counts?.[TAB_STATUS.suspended],
    blocked: counts?.[TAB_STATUS.blocked],
    all,
  };
}

/** The view after choosing another tab: its first page, the filters kept. */
export function withTab(search: ListSearch, tab: ListTab): ListSearch {
  const { tab: _, page: __, status, ...filters } = search;
  return {
    ...(tab === DEFAULT_TAB ? {} : { tab }),
    ...filters,
    ...(tab === "all" && status !== undefined ? { status } : {}),
  };
}

/** The view after a filter changed: the first page of the same tab. */
export function withFilters(
  search: ListSearch,
  filters: {
    area?: string | undefined;
    profession?: string | undefined;
    online?: string | undefined;
    status?: string | undefined;
  },
): ListSearch {
  const next: ListSearch = {};
  if (search.tab !== undefined) next.tab = search.tab;
  if (filters.area !== undefined) next.area = filters.area;
  if (filters.profession !== undefined) next.profession = filters.profession;
  if (filters.online === "yes" || filters.online === "no") next.online = filters.online;
  const status = SPECIALIST_STATUSES.find((known) => known === filters.status);
  if (status !== undefined) next.status = status;
  return next;
}

export function withPage(search: ListSearch, page: number): ListSearch {
  const { page: _, ...rest } = search;
  return page > 1 ? { ...rest, page } : rest;
}
